import { EBA_CERT_ANCHOR_COMPARATOR_VERSION } from './eba-cert-anchor';
import { EBA_CERT_MATRIX_GENERATOR_VERSION } from './eba-cert-matrix';
import { EBA_CERT_REDUCER_VERSION } from './eba-cert-reducer';

/**
 * Accepted two-oracle cert-engine version triples.
 *
 * A pay_rule_certification_run stamps `summary.versions = {matrixGenerator, reducer,
 * anchorComparator}` from the deployed engine constants. The platform gates provider-managed
 * EA payroll (pay-run snapshot, activation, and every pay_run_item write) on the run's triple
 * being TRUSTED. When the cert engine changes, the constants bump — and any gate that pins the
 * OLD triple by exact match would reject versions certified by the other generation.
 *
 * That is precisely the drift PR #1109 (E4) introduced: it bumped matrixGenerator 1.0.4->1.0.5
 * and reducer 1.0.5->1.0.6 without widening the acceptors, which (a) blocked activation of any
 * new version and (b) blocked pay-run snapshot for the two grandfathered ACTIVE versions still
 * stamped 1.0.4/1.0.5/1.0.1. This module is the single source of truth that WIDENS acceptance to
 * both generations.
 *
 * MUST stay in sync with the DB accepted-set in
 * `supabase/migrations/20434343100000_accept_half_cent_rounding_versions.sql` (latest redefinition; applied 2026-10-08)
 * (`public._eba_cert_summary_versions_accepted`). The guard test
 * `eba-cert-accepted-versions.test.ts` asserts (1) the currently-deployed triple is a member of
 * this set and (2) the migration text lists exactly these triples — so this class of drift fails
 * CI instead of production.
 *
 * Note: no NEW cert run can ever stamp the pre-E4 triple (the deployed engine only produces the
 * current triple), so the pre-E4 entry exists purely to grandfather the frozen runs of versions
 * certified before E4. It can be dropped once those versions are superseded + the DB set narrowed.
 */
// First independently versioned accepted-set contract: admits the GS additive
// daily-OT triple while preserving all grandfathered certificates. This is
// source metadata only; it does not enter stored rule or pay-context hashes.
// 1.0.1 — bind the source contract to reassigned slot 20326; accepted triples unchanged.
// 1.0.5 — admits reducer 1.0.17 (2026-10-08 exact half-up rounding); DB set widened by
// 20434343100000_accept_half_cent_rounding_versions.sql (applied 2026-10-08).
// 1.0.6 — admits reducer 1.0.18 (2026-10-08 overtime back-out rounds once, RND-04); DB set
// widened by 20434343400000_accept_reducer_1_0_18_overtime_backout.sql (applied 2026-10-08).
export const EBA_CERT_ACCEPTED_SET_VERSION = '1.0.6';

export interface EbaCertVersionTriple {
  matrixGenerator: string;
  reducer: string;
  anchorComparator: string;
}

export const ACCEPTED_EBA_CERT_VERSION_TRIPLES: readonly EbaCertVersionTriple[] = [
  // pre-E4 engine — grandfathers versions certified before PR #1109
  { matrixGenerator: '1.0.4', reducer: '1.0.5', anchorComparator: '1.0.1' },
  // post-E4 engine — PR #1109 category-scoped shift-loading certification (current constants)
  { matrixGenerator: '1.0.5', reducer: '1.0.6', anchorComparator: '1.0.1' },
  // pay-run programme wave 4 (16 Sep 2026) — reducer 1.0.9 certifies overtime on the loaded
  // shift rate (GS EA cl 32(c)(i), Nurses EA cl 22.22); DB set widened in
  // supabase/migrations/20272750000005_eba_cert_version_gate_widen_wave4.sql
  { matrixGenerator: '1.0.5', reducer: '1.0.9', anchorComparator: '1.0.1' },
  // P49 engine round 3 — reducer 1.0.11 shift-loading premium honours includesCasualLoading
  { matrixGenerator: '1.0.5', reducer: '1.0.11', anchorComparator: '1.0.1' },
  // Release Two (2026-09-17, Nurses EA overtime rate-basis re-derivation, 7df45997a) — reducer
  // bumped 1.0.11 -> 1.0.12 for a genuine math change: an OT fragment in a shift-loading band no
  // longer refuses (SHIFT_LOADING_OVERTIME_SUBSTITUTION_NOT_L1_VERIFIABLE) when the compiled
  // overtime rule is not on the loaded rate; it prices at zero shift-loading premium instead,
  // matching the engine. DB set widened in
  // supabase/migrations/20272760000000_eba_cert_version_gate_widen_release_two.sql
  { matrixGenerator: '1.0.5', reducer: '1.0.12', anchorComparator: '1.0.1' },
  // Round 4 shipped candidate: consistent daily-pair settlement normalization.
  { matrixGenerator: '1.0.7', reducer: '1.0.16', anchorComparator: '1.0.1' },
  // 2026-10-08 half-cent rounding — reducer 1.0.17 mirrors the engines' exact half-up
  // roundMoney. DB set widened (additively) in
  // supabase/migrations/20434343100000_accept_half_cent_rounding_versions.sql (applied 2026-10-08).
  { matrixGenerator: '1.0.7', reducer: '1.0.17', anchorComparator: '1.0.1' },
  // 2026-10-08 RND-04 — reducer 1.0.18 mirrors the engines' single-rounding overtime
  // back-out. DB set widened (additively) in
  // supabase/migrations/20434343400000_accept_reducer_1_0_18_overtime_backout.sql (applied 2026-10-08).
  { matrixGenerator: '1.0.7', reducer: '1.0.18', anchorComparator: '1.0.1' },
];

/** The triple a freshly-recorded cert run stamps under the currently-deployed engine constants. */
export const CURRENT_EBA_CERT_VERSION_TRIPLE: EbaCertVersionTriple = {
  matrixGenerator: EBA_CERT_MATRIX_GENERATOR_VERSION,
  reducer: EBA_CERT_REDUCER_VERSION,
  anchorComparator: EBA_CERT_ANCHOR_COMPARATOR_VERSION,
};

/**
 * True when a cert run's `summary.versions` object matches one of the accepted triples. Returns a
 * strict boolean (never undefined) for absent/malformed input — mirrors the DB helper's COALESCE.
 */
export function isAcceptedEbaCertVersionTriple(
  versions:
    | { matrixGenerator?: unknown; reducer?: unknown; anchorComparator?: unknown }
    | null
    | undefined,
): boolean {
  if (!versions || typeof versions !== 'object' || Array.isArray(versions)) return false;
  return ACCEPTED_EBA_CERT_VERSION_TRIPLES.some(
    (triple) =>
      versions.matrixGenerator === triple.matrixGenerator &&
      versions.reducer === triple.reducer &&
      versions.anchorComparator === triple.anchorComparator,
  );
}
